import { useForm } from 'react-hook-form';
import {
  TextField,
  Button,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  InputAdornment,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Person, Email, School, Badge } from '@mui/icons-material';
import { formButtonStyles } from '@/styles/formStyles';
import { RegisterData } from '@/services/api/domains/auth';
import { UserRole } from '@/types/models';

const registerSchema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  role: z.enum(['student', 'teacher', 'organization'] as [UserRole, ...UserRole[]]),
  school_code: z.string().optional(),
  district_domain: z.string().default('dreamsconnect.org'), // Default domain
});

type RegisterFormData = z.infer<typeof registerSchema>;

interface RegisterFormProps {
  onSubmit: (data: RegisterData) => Promise<void>;
}

export default function RegisterForm({ onSubmit }: RegisterFormProps) {
  const theme = useTheme();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      district_domain: 'dreamsconnect.org'
    }
  });

  const role = watch('role');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <TextField
          {...register('first_name')}
          label="First Name"
          error={!!errors.first_name}
          helperText={errors.first_name?.message}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          {...register('last_name')}
          label="Last Name"
          error={!!errors.last_name}
          helperText={errors.last_name?.message}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          {...register('email')}
          label="Email"
          error={!!errors.email}
          helperText={errors.email?.message}
          fullWidth
          type="email"
          autoComplete="email"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          {...register('password')}
          label="Password"
          type="password"
          error={!!errors.password}
          helperText={errors.password?.message}
          fullWidth
          autoComplete="new-password"
        />
        <FormControl fullWidth error={!!errors.role}>
          <InputLabel>Role</InputLabel>
          <Select
            {...register('role')}
            label="Role"
            defaultValue=""
            startAdornment={
              <InputAdornment position="start">
                <Badge color="action" />
              </InputAdornment>
            }
          >
            <MenuItem value="student">Student</MenuItem>
            <MenuItem value="teacher">Teacher</MenuItem>
            <MenuItem value="organization">Organization</MenuItem>
          </Select>
        </FormControl>
        {(role === 'student' || role === 'teacher') && (
          <TextField
            {...register('school_code')}
            label="School Code"
            error={!!errors.school_code}
            helperText={errors.school_code?.message}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <School color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isSubmitting}
          fullWidth
          sx={formButtonStyles(theme)}
        >
          {isSubmitting ? 'Creating Account...' : 'Create Account'}
        </Button>
      </Stack>
    </form>
  );
}